import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import LeftIcon from '../../../assets/images/left-arw.png';
import RightIcon from '../../../assets/images/right-arw.png';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReactSelect from 'react-select';
import {
  useCategoriesSellerServices,
  useCreateAppointment,
  useGetBuyerContacts,
  useGetClients,
  useGetWeeklySchedule,
} from '../../../hooks';
import { format } from 'date-fns';
import { showError, showSuccess } from '../../../utils';
import { Checkbox } from '../../atoms';
import { WheelTimePicker } from '../../atoms/time-picker/time-picker';
import { calendarViewAppointmentBookingSchema } from '../../../constants/calendar-view-appointment-validation';
import moment from 'moment';

function convertToAbbreviation(fullDayName) {
  // Define an array mapping full day names to their abbreviated forms
  var daysMapping = {
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
  };

  // Return the abbreviated form of the day if it exists in the mapping, otherwise return the input
  return daysMapping[fullDayName] || fullDayName;
}

// Define the order of days of the week
const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const sortArrayByDayName = (data) => {
  // Create a mapping of dayName to its index in the daysOfWeek array
  const dayIndexMap = {};
  daysOfWeek.forEach((day, index) => {
    dayIndexMap[day] = index;
  });

  // Sort the array using a custom comparator function
  data.sort((a, b) => {
    const dayIndexA = dayIndexMap[a.shiftDayName?.toLowerCase()];
    const dayIndexB = dayIndexMap[b.shiftDayName?.toLowerCase()];
    return dayIndexA - dayIndexB;
  });

  return data;
};

const formatDateOnly = (value) => {
  return moment(value).format('YYYY-MM-DD');
};

const displayDate = (dateString) => {
  return moment(dateString).format('MMMM D, YYYY');
};

const buildStartTimeIso = ({ selectedDate, timeHHMM }) => {
  if (!selectedDate || !timeHHMM) return '';
  return moment(
    `${selectedDate} ${timeHHMM}`,
    'YYYY-MM-DD HH:mm'
  ).toISOString();
};

const CalenderViewAppointmentForm = ({
  activeDate,
  closeFirstModal,
  setIsLoading,
  refetch,
  openSecondModal,
  newClientData,
  setAppointmentData,
  businessHoursData,
}) => {
  const ref = useRef(null);
  const storedData = sessionStorage.getItem('storefrontData');
  const parsedData = storedData ? JSON.parse(storedData) : null;
  const subscriptionExpiryDate = new Date(parsedData?.subscriptionExpiryDate);
  const futureBookingsEnabled = parsedData?.futureBookingsEnabled;

  const [filter, setFilter] = useState({
    dateRange: {
      fromDate: '2024-04-28',
      toDate: '2024-05-05',
    },
  });
  // const currentDate = format(new Date(), 'yyyy-MM-dd');
  const [scheduleData, setScheduleData] = useState([]);
  const { data: weeklyData, loading } = useGetWeeklySchedule(filter);
  const getCurrentWeekCount = (date) => {
    const now = date || new Date();
    const yearStart = new Date(now.getFullYear(), 0, 0);
    const diff = now - yearStart;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const weekCount = Math.floor(diff / oneWeek);
    return weekCount;
  };
  const [servicesData, setServicesData] = useState([]);
  const [selectedServiceUid, setSelectedServiceUid] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [clientList, setClientList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    activeDate ? format(activeDate, 'yyyy-MM-dd') : ''
  ); // Set the default selected date
  const [weekCursorDate, setWeekCursorDate] = useState(
    activeDate
      ? moment(activeDate).startOf('isoWeek')
      : moment().startOf('isoWeek')
  );

  const [selectedBuyerContactId, setSelectedBuyerContactId] = useState(null);
  const [buyerContactList, setBuyerContactList] = useState([]);

  const { data: clientData, refetch: refetchClients } = useGetClients({
    pagination: { page: 1, perPage: 500 },
  });
  const { data: buyerData, refetch: contactRefetch } =
    useGetBuyerContacts(selectedClient);

  const { data: sellerServices } = useCategoriesSellerServices();

  const handleCreateAppointment = useCreateAppointment();

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState('');
  const [pendingOverrideArgs, setPendingOverrideArgs] = useState(null);

  useImperativeHandle(ref, () => ({
    focus: () => ref.current.focus(), // Expose a focus function
  }));

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(calendarViewAppointmentBookingSchema()), // Use yupResolver with your schema
    defaultValues: {
      client: '',
      service: '',
      time: '',
      emailNotify: false,
      mobileNotify: false,
    },
  });

  // Extract business hours start time for the selected date
  const getBusinessHoursStart = (dateStr) => {
    const histories = businessHoursData?.businessHoursHistories || [];
    // Find the history entry for the selected date
    const dayHistory = histories.find((d) => d.shiftDate === dateStr);
    if (dayHistory?.histories?.length) {
      const starts = dayHistory.histories
        .map((h) => h.startTime)
        .filter((v) => v && /^\d{1,2}:\d{2}$/.test(v));
      if (starts.length) return starts[0];
    }
    // Fallback: use any available start time or default 9am
    const allRanges = histories.flatMap((d) => d.histories || []);
    const starts = allRanges
      .map((h) => h.startTime)
      .filter((v) => v && /^\d{1,2}:\d{2}$/.test(v));
    return starts.length ? starts[0] : '09:00';
  };

  useEffect(() => {
    if (!activeDate) return;
    const hhmm = moment(activeDate).format('HH:mm');
    const dateStr = moment(activeDate).format('YYYY-MM-DD');
    // If time is midnight (00:00), use business hours start for selected date
    const timeToSet = hhmm === '00:00' ? getBusinessHoursStart(dateStr) : hhmm;
    setValue('time', timeToSet);
  }, [activeDate, setValue, businessHoursData]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    const startTime = buildStartTimeIso({
      selectedDate,
      timeHHMM: data.time,
    });

    if (!startTime) {
      setIsLoading(false);
      return showError('Please select a time');
    }
    const input = {
      startTime,
      notifyEmail: data?.emailNotify || false,
      notifySms: data?.mobileNotify || false,
    };
    const sellerServiceUid = selectedServiceUid;
    const buyerUid = selectedClient;
    const familyMemberUid = selectedBuyerContactId || null;
    const note = data?.note;
    try {
      const response = await handleCreateAppointment(
        input,
        sellerServiceUid,
        buyerUid,
        familyMemberUid,
        note,
        false
      );
      if (
        response?.data?.createAppointment &&
        response?.data?.createAppointment?.status === 'OK'
      ) {
        setAppointmentData?.([]);

        // Keep modal open and show loading while refetching
        const doRefetch = async () => {
          try {
            console.log('Starting refetch...');
            const result = await refetch();
            console.log('Refetch completed:', result);
            closeFirstModal();
            reset();
            showSuccess(`Appointment has been booked Successfully`);
          } catch (error) {
            console.error('Refetch error:', error);
            // Still close modal even if refetch fails
            closeFirstModal();
            reset();
            showSuccess(`Appointment has been booked Successfully`);
          } finally {
            setIsLoading(false);
          }
        };

        doRefetch();
      } else {
        setIsLoading(false);
        const message = typeof response === 'string' ? response : '';
        const isOverrideCandidate =
          message.includes('Selected slots are not enough') ||
          message.includes('Slots are already being booked') ||
          message.includes("Appointment can't be booked for holiday");

        if (isOverrideCandidate) {
          setOverrideMessage(message);
          setPendingOverrideArgs({
            input,
            sellerServiceUid,
            buyerUid,
            familyMemberUid,
            note,
          });
          setOverrideModalOpen(true);
          return;
        }

        showError(response);
        console.log('Appointment creation failed');
      }
    } catch (error) {
      console.error('Appointment creation error:', error);
    }
  };

  const closeOverrideModal = () => {
    setOverrideModalOpen(false);
    setOverrideMessage('');
    setPendingOverrideArgs(null);
  };

  const confirmOverride = async () => {
    if (!pendingOverrideArgs) return;
    setIsLoading(true);
    try {
      const response = await handleCreateAppointment(
        pendingOverrideArgs.input,
        pendingOverrideArgs.sellerServiceUid,
        pendingOverrideArgs.buyerUid,
        pendingOverrideArgs.familyMemberUid,
        pendingOverrideArgs.note,
        true
      );
      if (
        response?.data?.createAppointment &&
        response?.data?.createAppointment?.status === 'OK'
      ) {
        setAppointmentData?.([]);
        closeFirstModal();
        refetch();
        reset();
        showSuccess(`Appointment has been booked Successfully`);
        closeOverrideModal();
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      showError(response);
    } catch (e) {
      setIsLoading(false);
      showError('Failed to schedule appointment');
    }
  };

  // Function to handle clicking on the left arrow
  const goToPreviousWeek = () => {
    setWeekCursorDate((prev) =>
      moment(prev).subtract(1, 'week').startOf('isoWeek')
    );
  };
  const currentDate = new Date();
  const ninetyDaysLater = new Date();
  ninetyDaysLater.setDate(currentDate.getDate() + 90);

  // Function to handle clicking on the right arrow
  const goToNextWeek = () => {
    setWeekCursorDate((prev) => moment(prev).add(1, 'week').startOf('isoWeek'));
  };

  useEffect(() => {
    if (activeDate) {
      setSelectedDate(format(activeDate, 'yyyy-MM-dd'));
    }
  }, [activeDate]);

  useEffect(() => {
    const startDate = moment(weekCursorDate).startOf('isoWeek');
    const endDate = moment(weekCursorDate).endOf('isoWeek');

    setFilter({
      dateRange: {
        fromDate: startDate.format('YYYY-MM-DD'),
        toDate: endDate.format('YYYY-MM-DD'),
      },
    });
  }, [weekCursorDate]);

  useEffect(() => {
    if (weeklyData) {
      const sortedData = sortArrayByDayName(weeklyData?.sellerScheduleStatuses);
      const currentDate = new Date();
      const ninetyDaysLater = new Date();
      ninetyDaysLater.setDate(currentDate.getDate() + 90); // Set the date to 90 days later

      setScheduleData(
        sortedData.map((item) => {
          const shiftDate = new Date(item.shiftDate);
          const newItem = { ...item };
          if (!futureBookingsEnabled) {
            newItem.enabled =
              shiftDate <= subscriptionExpiryDate &&
              shiftDate <= ninetyDaysLater;
          } else {
            newItem.enabled = shiftDate <= ninetyDaysLater;
          }
          return newItem;
        })
      );
    }
  }, [weeklyData, futureBookingsEnabled]);

  useEffect(() => {
    if (clientData?.buyers) {
      setClientList(clientData?.buyers?.nodes);
    }
  }, [clientData]);

  useEffect(() => {
    if (buyerData?.buyerFamilyMembers) {
      setBuyerContactList(buyerData?.buyerFamilyMembers);
    }
  }, [buyerData]);

  useEffect(() => {
    if (sellerServices) {
      setServicesData(sellerServices?.categoriesSellerServices);
    }
  }, [sellerServices]);

  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    contactRefetch();
  }, [selectedClient, contactRefetch]);

  const isCurrentWeekInSchedule = () => {
    const currentWeekStart = moment().startOf('isoWeek');
    const cursorWeekStart = moment(weekCursorDate).startOf('isoWeek');

    return cursorWeekStart.isSame(currentWeekStart, 'day');
  };

  const customStyles = {
    option: (provided) => ({
      ...provided,
      whiteSpace: 'pre-wrap', // Allows text to wrap
      wordBreak: 'break-word',
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontWeight: 'bold', // Make the group heading bold
      color: '#000',
    }),
  };

  const customSearchInput = ({ ...props }) => {
    return (
      <input
        {...props}
        maxLength={100}
        style={{ width: '100%', maxWidth: '100%', padding: '2px 8px' }}
      /> // Set the input style
    );
  };

  useEffect(() => {
    // Set the selectedClient to the UID of the newly added client
    if (newClientData?.uid) {
      const newClient = {
        uid: newClientData.uid,
        formalName: newClientData.formalName,
      };
      // Add new client to list immediately so it appears in dropdown
      setClientList((prev) => {
        const exists = prev.some((c) => c.uid === newClient.uid);
        return exists ? prev : [newClient, ...prev];
      });
      // Pre-select the new client
      setSelectedClient(newClient.uid);
      setValue('client', newClient.uid);
      // Also refetch to ensure list is in sync
      refetchClients();
    }
  }, [newClientData, setValue, refetchClients]);

  return (
    <>
      <form className="book-appointment" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="font-semibold text-sm">Select Client</label>
          <div className="flex gap-3 items-center">
            <Controller
              name="client"
              control={control}
              value={clientList.find((client) => client.uid === selectedClient)}
              render={({ field }) => {
                const selectedOption = clientList.find(
                  (client) => client.uid === field.value
                );
                return (
                  <ReactSelect
                    className="react-select-container text-sm w-full"
                    classNamePrefix="react-select"
                    options={[
                      { label: '+ Add New Client', value: '__ADD_NEW__' },
                      ...clientList.map((client) => ({
                        label: client.formalName,
                        value: client.uid,
                      })),
                    ]}
                    placeholder="Select Client"
                    styles={{
                      ...customStyles,
                      option: (provided, state) => ({
                        ...provided,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        ...(state.data.value === '__ADD_NEW__' && {
                          color: '#3B82F6',
                          fontWeight: 600,
                        }),
                      }),
                    }}
                    value={
                      selectedOption
                        ? {
                            label: selectedOption.formalName,
                            value: selectedOption.uid,
                          }
                        : null
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption.value === '__ADD_NEW__') {
                        openSecondModal();
                        return;
                      }
                      setSelectedClient(selectedOption.value);
                      field.onChange(selectedOption.value);
                    }}
                    components={{ Input: customSearchInput }}
                  />
                );
              }}
            />
          </div>
          {errors && errors?.client && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625 ">
              {errors?.client?.message}
            </p>
          )}
        </div>
        <div className="my-4">
          <Controller
            name="service"
            control={control}
            defaultValue={selectedServiceUid} // Set the default value to the selectedServiceUid state
            render={({ field }) => (
              <ReactSelect
                className="react-select-container text-sm w-full"
                classNamePrefix="react-select"
                options={servicesData.map((category) => ({
                  label: category.name,
                  options: category.sellerServices.map((service) => ({
                    value: service.uid,
                    label: service.name,
                  })),
                }))}
                placeholder="Select Service"
                styles={customStyles}
                onChange={(selectedOption) => {
                  setSelectedServiceUid(selectedOption.value); // Update the selectedServiceUid state
                  field.onChange(selectedOption.value); // Trigger the field's onChange event for react-hook-form
                }}
                components={{ Input: customSearchInput }} // Custom input component
              />
            )}
          />
          {errors && errors?.service && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625 ">
              {errors?.service?.message}
            </p>
          )}
        </div>
        <div className="md:my-4 my-2">
          <div className="calander-box md:mt-8 mt-0 md:w-full md:block">
            <div className="flex gap-4 justify-between items-center w-full">
              <p className="md:text-base text-xs font-semibold">
                {selectedDate
                  ? displayDate(selectedDate)
                  : 'Please Select Date'}
              </p>
              <div className="flex gap-4">
                <img
                  src={LeftIcon}
                  alt="icon"
                  className=" cursor-pointer"
                  style={{ opacity: isCurrentWeekInSchedule() && 0.3 }}
                  onClick={() => {
                    if (!isCurrentWeekInSchedule()) {
                      goToPreviousWeek();
                    }
                  }}
                />
                {/* <img
                  src={RightIcon}
                  alt="icon"
                  className={`cursor-pointer ${
                    futureBookingsEnabled
                      ? ''
                      : currentWeek >= expiryWeekCount
                      ? 'opacity-40 cursor-not-allowed'
                      : ''
                  }`}
                  onClick={() => {
                    if (futureBookingsEnabled) {
                      goToNextWeek();
                    } else {
                      if (currentWeek >= expiryWeekCount) {
                        return;
                      } else {
                        goToNextWeek();
                      }
                    }
                  }}
                /> */}
                <img
                  src={RightIcon}
                  alt="icon"
                  className={`cursor-pointer ${
                    futureBookingsEnabled &&
                    getCurrentWeekCount(weekCursorDate.toDate()) <
                      getCurrentWeekCount(ninetyDaysLater)
                      ? ''
                      : ''
                  }`}
                  onClick={() => {
                    if (futureBookingsEnabled) {
                      goToNextWeek();
                    } else {
                      goToNextWeek();
                    }
                  }}
                />
              </div>
            </div>
            <div className="calendar-date flex md:gap-3 gap-1 flex-wrap items-center mt-3">
              {scheduleData?.map((item, index) => {
                const currentDate = format(new Date(), 'yyyy-MM-dd');
                const isFutureDate = item.shiftDate >= currentDate;
                return (
                  <div
                    key={index}
                    onClick={() =>
                      isFutureDate && setSelectedDate(item?.shiftDate)
                    }
                    className={`grid border border-purple-bdr rounded-xl md:p-2 p-1 py-2 text-center md:w-45 w-9 ${
                      selectedDate === item?.shiftDate && item?.enabled
                        ? 'border-blue-bar bg-blue-calBg cursor-pointer'
                        : !item?.enabled || !isFutureDate
                        ? isFutureDate
                          ? 'opacity-60 hover:border-blue-bar hover:bg-blue-calBg cursor-pointer'
                          : 'opacity-40 cursor-not-allowed'
                        : 'hover:border-blue-bar hover:bg-blue-calBg cursor-pointer'
                    }`}
                  >
                    <p className="text-purple-light text-xs">
                      {convertToAbbreviation(item?.shiftDayName)}
                    </p>
                    <p className="text-black-main font-semibold md:text-base text-sm">
                      {item?.shiftDate?.split('-')[2]}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="md:my-4 my-2">
          <label className="text-sm  text-gray-dark">Time:</label>
          <Controller
            name="time"
            control={control}
            defaultValue="" // provide a default value if necessary
            render={({ field }) => (
              <WheelTimePicker
                value={field.value}
                onChange={(val) => field.onChange(val)}
                placeholder="Select time"
              />
            )}
          />
          {errors && errors?.time && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625 ">
              {errors?.time?.message}
            </p>
          )}
        </div>
        {overrideModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white-main w-11/12 md:w-96 rounded p-5 relative">
              <button
                type="button"
                className="absolute right-3 top-2 font-semibold"
                onClick={closeOverrideModal}
              >
                &#x2715;
              </button>
              <h3 className="font-semibold text-base">Schedule anyway?</h3>
              <p className="text-sm text-gray-dark mt-3">
                This time conflicts with your schedule (overlap, outside hours,
                or holiday).
              </p>
              {overrideMessage && (
                <p className="text-xs text-gray-middle mt-2 break-words">
                  {overrideMessage}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <button
                  type="button"
                  className="h-10 w-full border border-gray-dark bg-very-light text-gray-dark text-xs font-bold rounded-md"
                  onClick={closeOverrideModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="h-10 w-full bg-primary text-white-main text-xs font-bold rounded-md"
                  onClick={confirmOverride}
                >
                  Schedule anyway
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="md:my-4 my-2">
          <label className="text-sm  text-gray-dark">Note:</label>
          <Controller
            name="note"
            control={control}
            defaultValue="" // provide a default value if necessary
            render={({ field }) => (
              <textarea
                {...field}
                className="w-full text-xs font-medium rounded border border-gray-light console-input p-2 my-3"
                rows="4"
                cols="50"
              />
            )}
          />
          {errors && errors?.time && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625 ">
              {errors?.time?.message}
            </p>
          )}
        </div>
        <div className="md:mb-4 mb-1">
          <label className="md:text-sm text-xs text-black-main font-semibold">
            Please select your preferred notification method (one option
            required) <span className="text-red-drk">*</span>
          </label>
          <div className="flex gap-4">
            <Controller
              name="emailNotify"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  label={'Email'}
                  name={field?.name}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />

            <Controller
              name="mobileNotify"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  label={'SMS'}
                  name={field?.name}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          </div>
          {(errors.emailNotify || errors.mobileNotify) && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625">
              {errors.emailNotify.message || errors.mobileNotify.message}
            </p>
          )}
          <p className="md:text-sm text-xs">
            By selecting one or both options above, you agree to our{' '}
            <a
              href="https://salonhq.co/privacy-policy/"
              target="_blank"
              rel="noreferrer"
              className="md:text-sm text-xs text-blue-deep font-semibold underline"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="https://salonhq.co/terms-of-service-sms/"
              target="_blank"
              rel="noreferrer"
              className="md:text-sm text-xs text-blue-deep font-semibold underline"
            >
              SMS Policy
            </a>
            .
          </p>
        </div>

        <button className="h-10 w-full border mt-4 bg-primary text-white-main text-xs hover:bg-blue-hover py-2 text-center font-bold rounded-md">
          Book an appointment
        </button>
      </form>
    </>
  );
};

export default CalenderViewAppointmentForm;
